/**
 * Internationalisation catalog (Phase 1 config layer).
 *
 * Config-driven, tenant-agnostic reference data. Adding a new country or
 * language is a DATA change here (one entry) — no application logic changes
 * required. Operator-selected values are stored per-tenant in operator_settings.
 *
 * This is purely configuration/presentation. It does NOT touch dispatch,
 * lifecycle, pricing, or the frozen Voice AI booking logic.
 */

// Each country carries sensible regional defaults the wizard applies on select
// (still fully editable by the operator afterwards).
export const COUNTRIES = [
  { code: "GB", name: "United Kingdom", flag: "🇬🇧", currency: "GBP", timezone: "Europe/London",   dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "postcode" },
  { code: "IE", name: "Ireland",        flag: "🇮🇪", currency: "EUR", timezone: "Europe/Dublin",   dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "mixed" },
  { code: "ZW", name: "Zimbabwe",       flag: "🇿🇼", currency: "USD", timezone: "Africa/Harare",   dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "landmark" },
  { code: "ZA", name: "South Africa",   flag: "🇿🇦", currency: "ZAR", timezone: "Africa/Johannesburg", dateFormat: "YYYY/MM/DD", timeFormat: "24h", addressingMode: "mixed" },
  { code: "KE", name: "Kenya",          flag: "🇰🇪", currency: "KES", timezone: "Africa/Nairobi",  dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "landmark" },
  { code: "NG", name: "Nigeria",        flag: "🇳🇬", currency: "NGN", timezone: "Africa/Lagos",    dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "landmark" },
  { code: "AE", name: "United Arab Emirates", flag: "🇦🇪", currency: "AED", timezone: "Asia/Dubai", dateFormat: "DD/MM/YYYY", timeFormat: "12h", addressingMode: "landmark" },
  { code: "US", name: "United States",  flag: "🇺🇸", currency: "USD", timezone: "America/New_York", dateFormat: "MM/DD/YYYY", timeFormat: "12h", addressingMode: "mixed" },
  { code: "IN", name: "India",          flag: "🇮🇳", currency: "INR", timezone: "Asia/Kolkata",    dateFormat: "DD/MM/YYYY", timeFormat: "12h", addressingMode: "landmark" },
  { code: "FR", name: "France",         flag: "🇫🇷", currency: "EUR", timezone: "Europe/Paris",    dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "postcode" },
  { code: "PT", name: "Portugal",       flag: "🇵🇹", currency: "EUR", timezone: "Europe/Lisbon",   dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "postcode" },
  { code: "ES", name: "Spain",          flag: "🇪🇸", currency: "EUR", timezone: "Europe/Madrid",   dateFormat: "DD/MM/YYYY", timeFormat: "24h", addressingMode: "postcode" },
];

// Supported + roadmapped languages. `enabled:false` = advertised as coming soon
// (selectable as a "supported customer language" but flagged for Phase 2 Voice AI).
export const LANGUAGES = [
  { code: "en", name: "English",    native: "English",    flag: "🇬🇧", enabled: true },
  { code: "sn", name: "Shona",      native: "chiShona",    flag: "🇿🇼", enabled: true },
  { code: "nd", name: "Ndebele",    native: "isiNdebele",  flag: "🇿🇼", enabled: true },
  { code: "af", name: "Afrikaans",  native: "Afrikaans",   flag: "🇿🇦", enabled: false },
  { code: "sw", name: "Swahili",    native: "Kiswahili",   flag: "🇰🇪", enabled: false },
  { code: "fr", name: "French",     native: "Français",    flag: "🇫🇷", enabled: false },
  { code: "pt", name: "Portuguese", native: "Português",   flag: "🇵🇹", enabled: false },
  { code: "es", name: "Spanish",    native: "Español",     flag: "🇪🇸", enabled: false },
  { code: "ar", name: "Arabic",     native: "العربية",     flag: "🇦🇪", enabled: false },
  { code: "hi", name: "Hindi",      native: "हिन्दी",       flag: "🇮🇳", enabled: false },
];

export const ADDRESSING_MODES = [
  { value: "postcode", label: "Postcode-first (UK style)", hint: "AI asks for a postcode; best for UK/EU." },
  { value: "landmark", label: "Landmark / suburb", hint: "AI asks for suburb, street or landmark; best where postcodes aren't used (e.g. Zimbabwe)." },
  { value: "mixed",    label: "Mixed", hint: "AI accepts postcode OR landmark/suburb." },
];

export const CURRENCIES = ["GBP", "EUR", "USD", "ZAR", "KES", "NGN", "AED", "INR"];
export const TIME_FORMATS = [{ value: "24h", label: "24-hour (14:30)" }, { value: "12h", label: "12-hour (2:30 PM)" }];
export const DATE_FORMATS = ["DD/MM/YYYY", "MM/DD/YYYY", "YYYY/MM/DD", "YYYY-MM-DD"];
export const TIMEZONES = [
  "Europe/London", "Europe/Dublin", "Europe/Paris", "Europe/Lisbon", "Europe/Madrid",
  "Africa/Harare", "Africa/Johannesburg", "Africa/Nairobi", "Africa/Lagos",
  "Asia/Dubai", "Asia/Kolkata", "America/New_York",
];

export const findCountry = (code) => COUNTRIES.find((c) => c.code === code) || null;
export const languageLabel = (code) => {
  const l = LANGUAGES.find((x) => x.code === code);
  return l ? `${l.flag} ${l.name}` : code;
};
